<template lang="html">
  <div
    :style="`height:100%;width:100%`"
  >
    <v-tooltip v-if="params('draft') === 'true'">
      <template v-slot:activator="{ on }">
        <v-btn
          icon
          style="position:absolute;top:24px;z-index:1"
          class="ml-4"
          v-on="on"
          @click="bboxPasted()"
        >
          <v-icon>mdi-crop-free</v-icon>
        </v-btn>
      </template>
      <span>Copier la position actuelle</span>
    </v-tooltip>
    <v-progress-linear
      v-if="loading"
      style="z-index:1;pointer-events: none;"
      indeterminate
      color="primary"
    />
    <div
      id="map"
      :style="`height: ${windowHeight-(loading ? 4 : 0)}px;margin-top:${!loading ? 4 : 0}px`"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import 'maplibre-gl/dist/maplibre-gl.css'
import axios from 'axios'
import { bbox2zoom } from '@data-fair/components-app/map/utils'

const maplibregl = require('maplibre-gl')

export default {
  data: () => ({
    loading: true,
    selected: null
  }),
  computed: {
    ...mapState(['datasets', 'codes']),
    ...mapGetters(['tileserverUrl', 'config', 'levelsInfos'])
  },
  watch: {
    codes () {
      if (!this.map.getSource('admin-divs')) return
      const options = this.config.colors.rangeOptions || this.config.colors.enumOptions
      const level = Object.keys(this.levelsInfos).find(level => this.levelsInfos[level][options.dataset.id])
      Object.entries(this.codes).forEach(([code, color]) => {
        this.map.setFeatureState({
          source: 'admin-divs',
          sourceLayer: level,
          id: code
        }, {
          color
        })
      })
    }
  },
  mounted () {
    this.initMap()
  },
  methods: {
    initMap () {
      if (!maplibregl) return
      const initialBounds = this.config.bbox && this.config.bbox.length && maplibregl.LngLatBounds.convert(JSON.parse(this.config.bbox))
      const params = new URLSearchParams(window.location.search)
      const zoom = params.get('zoom') ? Number(params.get('zoom')) : (initialBounds ? bbox2zoom(JSON.parse(this.config.bbox)) : 7)
      const center = params.get('lng') && params.get('lat') ? [Number(params.get('lng')), Number(params.get('lat'))] : (initialBounds ? initialBounds.getCenter().toArray() : [2.0833, 47.2167])
      this.map = new maplibregl.Map({
        container: 'map',
        center,
        zoom,
        style: `${this.tileserverUrl}/styles/${this.config.style}/style.json`,
        transformRequest: (url, resourceType) => {
          return {
            url,
            credentials: 'include' // include cookies, for data-fair sessions
          }
        }
      })
      this.map.addControl(new maplibregl.NavigationControl(), 'top-right')
      if (this.config.geolocate) this.initGeolocation()

      // Disable map rotation using right click + drag
      this.map.dragRotate.disable()
      // Disable map rotation using touch rotation gesture
      this.map.touchZoomRotate.disableRotation()

      this.map.on('click', (e) => this.select([e.lngLat.lng, e.lngLat.lat]))
      this.map.on('moveend', (e) => {
        this.loading = true
        const params = new URLSearchParams(window.location.search)
        params.set('zoom', e.target.getZoom())
        const { lat, lng } = e.target.getCenter()
        params.set('lng', lng)
        params.set('lat', lat)
        window.history.replaceState(null, null, window.location.pathname + '?' + params.toString())
      })
      this.map.once('load', async () => {
        this.map.addSource('admin-divs', { type: 'vector', tiles: [this.tileserverUrl + '/data/admin-divs-latest/{z}/{x}/{y}.pbf'], promoteId: 'code' })
        this.updateAdminDivsLayer(this.currentLevel)
        const params = new URLSearchParams(window.location.search)
        const selected = params.get('selected')
        const level = params.get('level')
        if (selected && level) {
          this.selected = {
            source: 'admin-divs',
            sourceLayer: level,
            id: selected
          }
          this.map.setFeatureState(this.selected, {
            selected: true
          })
          const selectedInfos = {}
          for (const lev of ['iris', 'communes', 'epci', 'departements', 'regions']) {
            if (this.levelsInfos[lev]) {
              for (const id of Object.keys(this.levelsInfos[lev])) {
                const params = { qs: `${this.levelsInfos[lev][id].key}:"${selected}"` }
                const data = (await axios.get(this.datasets[id].href + '/lines', { params })).data.results[0]
                selectedInfos[id] = data
              }
            }
          }
          this.$store.commit('setAny', { selectedInfos })
        }
      })

      // "idle" event seems to be the best indication that the map is fully loaded
      this.map.on('idle', () => {
        this.loading = false
      })
    },
    async  select (pos) {
      this.$store.commit('setAny', { selectedInfos: {} })
      if (this.selected) {
        this.map.setFeatureState(this.selected, {
          selected: false
        })
      }
      const features = this.map.queryRenderedFeatures(this.map.project(pos), {
        layers: Object.keys(this.levelsInfos).map(l => 'admin-divs-colors-' + l)
      })
      if (features.length) {
        const selectedInfos = {}
        let selected = false
        for (const level of ['iris', 'communes', 'epci', 'departements', 'regions']) {
          const feature = features.find(f => f.sourceLayer === level)
          if (feature) {
            if (this.selected && this.selected.id === feature.properties.code) {
              this.selected = null
              this.$store.commit('setAny', { selectedInfos: {} })
              const params = new URLSearchParams(window.location.search)
              params.delete('selected')
              params.delete('level')
              window.history.replaceState(null, null, window.location.pathname + '?' + params.toString())
              return
            } else {
              if (!selected) {
                this.selected = {
                  source: 'admin-divs',
                  sourceLayer: level,
                  id: feature.properties.code
                }
                this.map.setFeatureState(this.selected, {
                  selected: true
                })
                const params = new URLSearchParams(window.location.search)
                params.set('selected', feature.properties.code)
                params.set('level', level)
                window.history.replaceState(null, null, window.location.pathname + '?' + params.toString())
              }
              for (const id of Object.keys(this.levelsInfos[level])) {
                const params = { qs: `${this.levelsInfos[level][id].key}:"${feature.properties.code}"` }
                const data = (await axios.get(this.datasets[id].href + '/lines', { params })).data.results[0]
                selectedInfos[id] = data
              }
            }
            selected = true
          }
        }
        this.$store.commit('setAny', { selectedInfos })
      }
    },
    updateAdminDivsLayer (newLevel, oldLevel) {
      const labelsIdx = this.map.getStyle().layers.findLastIndex(l => l.type !== 'symbol')
      const labelsLayerId = this.map.getStyle().layers[labelsIdx + 1].id
      for (const level of Object.keys(this.levelsInfos)) {
        const paint = {
          'fill-color': ['string', ['feature-state', 'color'], this.config.colors.default || 'white'],
          'fill-opacity': 0
        }
        const options = this.config.colors.rangeOptions || this.config.colors.enumOptions
        if (this.levelsInfos[level][options.dataset.id]) {
          paint['fill-opacity'] = 0.8
          if (this.codes) {
            Object.entries(this.codes).forEach(([code, color]) => {
              this.map.setFeatureState({
                source: 'admin-divs',
                sourceLayer: level,
                id: code
              }, {
                color
              })
            })
          }
        }

        this.map.addLayer({
          id: 'admin-divs-colors-' + level,
          type: 'fill',
          source: 'admin-divs',
          'source-layer': level,
          paint
        }, labelsLayerId)
      }
      for (const level of Object.keys(this.levelsInfos)) {
        this.map.addLayer({
          id: 'admin-divs-lines-' + level,
          type: 'line',
          source: 'admin-divs',
          'source-layer': level,
          paint: {
            'line-color': ['case', ['boolean', ['feature-state', 'selected'], false], 'red', '#AAAAAA'],
            'line-width': 1,
            'line-offset': ['case', ['boolean', ['feature-state', 'selected'], false], 1, 0]
          }
        }, labelsLayerId)
      }
    },
    bboxPasted () {
      const bbox = JSON.stringify([].concat(...this.map.getBounds().toArray()).map(n => n.toFixed(6)))
      navigator.clipboard.writeText(bbox)
      if (window.parent) window.parent.postMessage({ type: 'set-config', content: { field: 'bbox', value: bbox } }, '*')
    },
    params (p) {
      const params = new URLSearchParams(window.location.search)
      return params.get(p)
    },
    initGeolocation () {
      this.geolocateControl = new maplibregl.GeolocateControl({
        showUserLocation: false,
        positionOptions: {
          enableHighAccuracy: true
        },
        fitBoundsOptions: {
          zoom: 11,
          animate: false
        }
      })
      this.map.addControl(this.geolocateControl, 'top-right')

      this.geolocateControl.on('error', err => {
        console.log('geolocate error', err)
        this.geolocateControl.remove()
      })
    }
  }
}
</script>

<style lang="css">
</style>
